import React, { useCallback, useState } from 'react'
import { useTranslation } from '@hooks/useTranslation'
import { Grid, Message } from 'semantic-ui-react'
import { InvoiceForm } from './InvoiceForm'
import { CustomLoader } from '@commonComponents/formComponents/loader'
import ToggleButton from '@commonComponents/formComponents/toggleButton'
import ErrorBoundary from '@commonComponents/ErrorBoundary'
import InvoiceSummary from './InvoiceSummary'
import { AddBtn } from '@commonComponents/AddBtn'
import Merchandise from '../merchandise/Merchandise'
import { useDispatch, useSelector } from 'react-redux'
import { itemActions } from '../../../reducer/ItemSlice'
import { useEvent } from '@hooks/useEvent'
import { selectCountryInfoDS, selectCountryList, selectHeaderTabDS, selectTransactionNatureList } from '@EUForms/selectors'
import { useCurrentShipmentType } from '@hooks/useCurrentShipmentType'
import { useContext } from 'react'
import { InvoiceToggleContext } from '@commonComponents/accordion/AccordianInvoice'
import { addExtraSpace } from '@EUForms/utils'
import MarkedDeleted from '../markedDeleted'


const Invoice = ({ invoiceId, invoiceIndex }) => {
  const { state } = useContext(InvoiceToggleContext)
  const active = state[invoiceId]
  const translate = useTranslation()
  const { isLoading, saveForm, deleteForm } = useEvent('invoiceTab', true)
  const dispatch = useDispatch()
  const { isImportShipment, isRFI, isRFIDisabled } = useCurrentShipmentType()
  const { 
    updateInvoiceHeader, 
    deleteInvoice, 
    createMerchandiseItem, 
    updateMerchandiseItem, 
    updateInvoiceUpperHeader,
    updateInvoiceHierarchy 
  } = itemActions
  const countryList = useSelector(selectCountryList)
  const originCountry = useSelector(selectCountryInfoDS)?.originCountry
  const dataSource = useSelector(
    (state) => state.invoiceReducer.invoice.invoiceMap[invoiceId].invoiceHeader
  )
  const isInvoiceDisabled = ((isRFI && dataSource?.markDelete=='1') || null)
  const procedure = useSelector(selectHeaderTabDS)?.procedure
  


  const deleteData = useCallback(async () => {
    const response = await deleteForm({ invoiceIdList: [invoiceId] })
    if (response) {
      dispatch(deleteInvoice({ invoiceId }))
    }
  }, [invoiceId, deleteInvoice, deleteForm])

  const saveData = useCallback(
    async (payload) => {
      const response = await saveForm(
        { invoiceMap: { [invoiceId]: { invoiceId, invoiceHeader: {...addExtraSpace(payload.data)} } } },
        false
      )
      if (response) {
        const invoiceUpperHeader = response?.invoiceTab?.invoiceUpperHeader
        invoiceUpperHeader && dispatch(updateInvoiceUpperHeader(invoiceUpperHeader))
        const invoiceMap = response?.invoiceTab?.invoiceMap
        const invoiceHeader = (invoiceMap && invoiceMap[invoiceId]?.invoiceHeader) || ''
        invoiceHeader && dispatch(updateInvoiceHeader({ invoiceId, data: invoiceHeader }))
        const merchandiseMap = response?.invoiceTab?.merchandiseMap
        if (merchandiseMap)
          for (let [key, val] of Object.entries(merchandiseMap)) {
            dispatch(updateMerchandiseItem({ merchandiseId: key, data: val }))
          }
      }
    },
    [saveForm, invoiceId]
  )
  const updateItem = useCallback(({ invoiceId, data }) => {
    dispatch(updateInvoiceHeader({ invoiceId, data }))
  }, [invoiceId])
  
  const addMerchandise = useCallback(async () => {
    const response = await saveForm({ merchandiseMap: { '': { invoiceId } } }, true)
    if (response) {
      const merchandiseId = response.invoiceTab.merchandiseIdList[0]
      dispatch(createMerchandiseItem({ 
        merchandiseId, 
        invoiceId, 
        procedure:'1000', 
        originCountry:originCountry 
      }))
    }
  }, [createMerchandiseItem, saveForm, invoiceId])

  const onMarkInvoiceDelete = useCallback((e, data) => {
    if (data.checked) {
      // updateItem({ invoiceId, data: { markDelete: '1' } })
      dispatch(updateInvoiceHierarchy({ invoiceId, data: { markDelete: '1' } }))
      saveData({ invoiceId, data: { markDelete: '1' } })
    } else {
      // updateItem({ invoiceId, data: { markDelete: '0' } })
      dispatch(updateInvoiceHierarchy({ invoiceId, data: { markDelete: '0' } }))
      saveData({ invoiceId, data: { markDelete: '0' } })
    }
  },[invoiceId])
  return (
    <ErrorBoundary>
      <div className={'accordian-section tab-contentInvoice '}>
        <Message>
          {isLoading && <CustomLoader />}
          <ToggleButton
            id={`toggleInvoice-${invoiceId}`}
            title={(isActive) =>
              isActive ? (
                'INVOICE'
              ) : (
                <InvoiceSummary id={invoiceId} dataSource={dataSource} translate={translate} />
              )
            }
            toggleId={invoiceId}
            // onClick={() => setActiveAccordianIndex(!activeAccordianIndex)}
            lineNumber={invoiceIndex + 1}
            // isActive={activeAccordianIndex}
            confirmationMsg='Invoice'
            onDelete={() => deleteData({ invoiceId })}
            buttonName='INVOICE'
            titleStyle={{ marginRight: '11.4rem' }}
            markedDeleted={isRFI?() => <MarkedDeleted
              label='Marked Deleted'
              className='marked-deleted'
              checked={dataSource?.markDelete=='1'}
              onChange={onMarkInvoiceDelete}
              disabled={isRFIDisabled}
            /> : null}
          />
          {active && (
            <>
              <InvoiceForm
                dataSource={dataSource}
                procedure={procedure}
                translate={translate}
                saveItem={saveData}
                updateItem={updateItem}
                countryList={countryList}
                isInvoiceDisabled={isInvoiceDisabled}
                // renderAtLast={renderAtLast}
              />
              <Merchandise 
                invoiceId={invoiceId} 
                invoiceIndex={invoiceIndex} 
                isInvoiceDisabled={isInvoiceDisabled}
              />
              <AddBtn 
                onClick={addMerchandise} 
                text='ADD LINE ITEM'
                size='tiny' 
                isDisabled={isInvoiceDisabled}
              />
            </>
          )}
        </Message>
      </div>
    </ErrorBoundary>
  )
}

export default React.memo(Invoice)
